import { PLAY_MODE, SET_LEVEL_MODE, VIDEO_EVTS } from '../enums';
import { videoTool } from '../util';
/**
 * VideoNode播放状态检查类
 */
var CheckVideoNodeStatus = /** @class */ (function () {
    function CheckVideoNodeStatus(vn) {
        this.videoNode = vn;
    }
    /**
   * 检查当前切换清晰度是否已经结束，以方便执行下一步骤
   * @param evtName 事件名，切换清晰度完成可能在多个事件中检查。
   * @returns 返回为空表示setLevel未结束，非空表示结束
   */
    CheckVideoNodeStatus.prototype.checkIsSetLevelOver = function (evtName, targetPlaytime) {
        var _a;
        if (evtName === void 0) { evtName = VIDEO_EVTS.playing; }
        if (targetPlaytime === void 0) { targetPlaytime = 0; }
        if (!((_a = this.videoNode) === null || _a === void 0 ? void 0 : _a.videoElement)) {
            throw new Error('Target VideoNode is null!');
        }
        if (!this.videoNode.getLevelSwitching()) {
            // 返回为空就是没有结束
            return null;
        }
        // 播放中检查
        var succInPlaying = false;
        // Seek后的检查
        var succInSeeked = false;
        // Timeupdate检查
        var succInTimeupdate = false;
        if (this.videoNode.config.type === PLAY_MODE.LIVE) {
            // 直播开播以后就直接判断切换清晰度成功，并且没有seek的情况
            succInPlaying = evtName === VIDEO_EVTS.playing;
            succInTimeupdate = evtName === VIDEO_EVTS.timeupdate;
        }
        else {
            var isMFSupport = videoTool.supportMediaFragmentsURISpec(this.videoNode.config.enableMediaFragmentsURI);
            // 支持#t的时候切换清晰度是直接在url带上了目标位置，开播即到达目标播放位置
            succInPlaying = evtName === VIDEO_EVTS.playing && isMFSupport;
            // 不支持#t标准的浏览器环境下，切换到下一个清晰度了还需要调用seek操作才能到达播放目标位置，因此在seeked状态检测
            succInSeeked = evtName === VIDEO_EVTS.seeked && !isMFSupport;
            // timeupdate中是兜底的检测，下一个清晰度的时间超过了容忍值0.3S切换时记录的时间则切换成功
            var toleranceMinGap = 0.3;
            var toleranceGap = this.videoNode.videoElement.currentTime - targetPlaytime >= toleranceMinGap;
            succInTimeupdate = evtName === VIDEO_EVTS.timeupdate && toleranceGap;
        }
        if (succInPlaying || succInSeeked || succInTimeupdate) {
            // 只要其中一个为true，就返回非空表示结束
            return {
                succInPlaying: succInPlaying,
                succInSeeked: succInSeeked,
                succInTimeupdate: succInTimeupdate,
            };
        }
        // 返回为空就是没有结束
        return null;
    };
    /**
     * 检查当前videonode是否可以切换清晰度
     * @returns {canSetLevel:boolean,message:string} canSetLevel表示是否能切换，message表示具体信息描述和不能切换原因
     */
    CheckVideoNodeStatus.prototype.checkCanSetLevel = function () {
        if (!this.videoNode) {
            return {
                canSetLevel: false,
                message: 'videoNode is null',
            };
        }
        if (!this.videoNode.played) {
            // 如果根本没能起播，是无法切换清晰度的
            return {
                canSetLevel: false,
                message: 'setLevel(), play is not triggered.',
            };
        }
        if (this.videoNode.getLevelSwitching()) {
            // 切换清晰度正在进行中，需要继续等待
            return {
                canSetLevel: false,
                message: 'setLevel(), in progress.',
            };
        }
        if (!this.videoNode.currentVideoInfo || this.videoNode.currentVideoInfo.playtime <= 0) {
            // 如果currentVideoInfo为空，也暂时无法切换清晰度
            return {
                canSetLevel: false,
                message: 'setLevel(), setLevel can\'t start.',
            };
        }
        if (this.videoNode.config.type === PLAY_MODE.VOD
            && this.videoNode.currentVideoInfo.playtime + 1 >= this.videoNode.currentVideoInfo.duration) {
            // 点播下如果播放接近结束（一秒内），可以认为切换清晰度是无意义的
            return {
                canSetLevel: false,
                message: 'setLevel(), video is about to end.',
            };
        }
        return {
            canSetLevel: true,
            message: 'setLevel(), can set level now',
        };
    };
    /**
     * 检查当前video是否能seek,在调用seek前检查
     * @param seekOpts seek相关信息
     * @returns {canSeek:boolean,message:string} canSeek表示是否能seek，message表示具体信息描述和不能seek原因
     */
    CheckVideoNodeStatus.prototype.checkCanSeek = function (seekOpts) {
        var _a;
        if (!((_a = this.videoNode) === null || _a === void 0 ? void 0 : _a.currentVideoInfo)) {
            // 如果vn或者vn.currentVideoInfo为空，无法Seek
            return {
                canSeek: false,
                message: 'seek(), because videonode or videoInfo is null',
            };
        }
        if (this.videoNode.videoElement.duration <= 0) {
            // 如果video元素duration小于等于0，不能seek。在MSE上会有拼接导致duration产生变化，所以没有使用currentVideoInfo
            return {
                canSeek: false,
                message: 'seek(), cannot seek when the duration is 0',
            };
        }
        var _b = seekOpts || {}, _c = _b.seektime, seektime = _c === void 0 ? NaN : _c, _d = _b.userActivation, userActivation = _d === void 0 ? false : _d, _e = _b.setLevelMode, setLevelMode = _e === void 0 ? SET_LEVEL_MODE.HARD : _e;
        if (Number.isNaN(seektime)) {
            // 如果没有传入seektime不，无法seek
            return {
                canSeek: false,
                message: 'target time is NaN',
            };
        }
        if (seektime - this.videoNode.currentVideoInfo.duration >= 1) {
            // seek比duration还多出1S，也不能seek
            // 为什么是1S？是因为以下原因，需要做保护性矫正，check执行后都会减去1S保证不出现上面问题：
            // 1. safari/x5下可能出现直接seek到视频的最结尾（seek时间等于视频时长）后，video不发出end事件出来，所以检测如果这种情况，把time值回退1s
            // 2. chrome环境seek到最最后面，可能会卡死在seeking状态
            return {
                canSeek: false,
                message: "seek(), return because too large time, uid=".concat(this.videoNode.currentVideoInfo.uid, "/idx=").concat(this.videoNode.idx, "/time=").concat(seektime)
                    + "/duration=".concat(this.videoNode.currentVideoInfo.duration),
            };
        }
        if (typeof setLevelMode !== 'undefined'
            && setLevelMode !== SET_LEVEL_MODE.SMOOTH
            && this.videoNode.getLevelSwitching()
            && userActivation) {
            // 非平滑切换时，“外部”不能seek（内部是可以seek的，例如某些浏览器切清晰度的时候，下一个清晰度需要seek到目标时间点）。
            // 因为加载下一个清晰度加了#t参数，如果当前清晰度进行seek操作，那么下一个清晰度加载ok以后跟上一个清晰度seek后的时间就无法对齐
            return {
                canSeek: false,
                message: 'seek(), cannot seek when hard/soft level switching',
            };
        }
        return {
            canSeek: true,
            message: 'seek(), can seek now',
        };
    };
    /**
     * 检查是否能设置音轨
     * @param audioTrackOpts 音轨相关信息
     * @returns {canSetAudioTrack:boolean,message:string} canSetAudioTrack表示是否能设置音轨，message表示具体信息描述和不能设置原因
     */
    CheckVideoNodeStatus.prototype.checkCanSetAudioTrack = function (audioTrackDefer) {
        if (audioTrackDefer) {
            // 当前正在切换中，返回不能切换
            return {
                canSetAudioTrack: false,
                message: 'setAudioTrack(), in progress',
            };
        }
        if (!this.videoNode) {
            // 如果vn为空，无法设置音轨
            return {
                canSetAudioTrack: false,
                message: 'setAudioTrack(), because videonode is null',
            };
        }
        if (!this.videoNode.hasAltAudio) {
            // 如果不存在可以切换的音轨，返回不能切换
            return {
                canSetAudioTrack: false,
                message: 'setAudioTrack(), current content does not contain alternative audio track',
            };
        }
        if (!this.videoNode.played) {
            // 如果vn并没有起播，无法切换音轨
            return {
                canSetAudioTrack: false,
                message: 'setAudioTrack(), can only be invoked after content plays',
            };
        }
        return {
            canSetAudioTrack: true,
            message: 'setAudioTrack(), can set audio track now',
        };
    };
    /** 销毁 */
    CheckVideoNodeStatus.prototype.destroy = function () {
        this.videoNode = null;
    };
    return CheckVideoNodeStatus;
}());
export default CheckVideoNodeStatus;
